import React, { FC } from 'react';
import { startCase, toLower } from 'lodash';
import SectionTitle from '@src/components/SectionTitle';
import {
  CompanyProfileFundingRoundTypeEnum,
  CurrencyType,
  useAvailableCurrenciesQueryQuery,
} from '@src/graphql/generated';
import { DropdownInput } from '@src/components/Select/DropdownInput';
import { TextField } from '@src/components/TextField';
import { LatestFundingProps } from './props';
import './styles.scss';

function formatFundingType(type: string) {
  const formatted = type.replace('_', ' ');

  return startCase(toLower(formatted)).replace('Vc', 'VC').replace('Ipo', 'IPO');
}

export const LatestFunding: FC<LatestFundingProps> = ({ data, onChange }) => {
  const types = ['ROUND_TYPE', ...Object.keys(CompanyProfileFundingRoundTypeEnum)];

  const { data: availableCurrenciesData } = useAvailableCurrenciesQueryQuery();
  const availableCurrencies = availableCurrenciesData?.availableCurrencies;

  const onRoundTypeChange = (round: string) => {
    if (round === 'ROUND_TYPE') {
      onChange({ ...data, value: null });

      return;
    }

    onChange({
      ...data,
      round: CompanyProfileFundingRoundTypeEnum[round as keyof typeof CompanyProfileFundingRoundTypeEnum],
    });
  };

  const onValueChange = (value: Number) => {
    onChange({ ...data, value: value });
  };

  const onFundedAtChange = (value: string) => {
    onChange({ ...data, fundedAt: value });
  };

  const onCurrencyChange = (value: CurrencyType) => {
    onChange({ ...data, currency: value });
  };

  const value = parseInt(data?.value) || undefined;

  return (
    <div className="latest-funding">
      <SectionTitle size="S" title="Latest funding" />

      <div className="latest-funding__container">
        <div className="fr-input fr-input__container fr-input__container--helper">
          <input
            type="date"
            className="fr-input__field latest-funding__date-input"
            value={data?.fundedAt ? new Date(data.fundedAt).toISOString().split('T')[0] : undefined}
            onChange={event => {
              onFundedAtChange(event.target.value);
            }}
          />
        </div>
        <DropdownInput
          onChange={value => {
            if (value) {
              onRoundTypeChange(value.value);
            }
          }}
          options={types?.map(item => ({ label: formatFundingType(item), value: item }))}
          placeholder="Funding type"
          value={
            data?.round
              ? { label: formatFundingType(data.round), value: data.round }
              : { label: formatFundingType('ROUND_TYPE'), value: 'ROUND_TYPE' }
          }
        />
      </div>
      <div className="latest-funding__container">
        <TextField clearable defaultValue={value} onChange={onValueChange} placeholder="Funding size" />
        <DropdownInput
          wrapperClassName="latest-funding__currency-dropdown"
          onChange={value => {
            if (value?.value && value?.label) {
              onCurrencyChange({ code: value.label, id: value.value, symbol: '' });
            }
          }}
          options={availableCurrencies?.map(item => ({ label: item.code, value: item.id }))}
          value={data?.currency ? { label: data.currency.code, value: data.currency.id } : undefined}
          placeholder="Currency"
        />
      </div>
    </div>
  );
};
